import React, { useState } from "react";

import { SingleTabScreen } from "@components/General/SingleTabScreen";
import { LiteVersionPopup } from "@components/General/ProhibitedPopups";
import { VirtualizedList } from "@components/General/VirtualizedList";
import { DeleteQontactPointModal } from "@components/QontactPoints/DeleteQontactPointModal";
import { NewQontactPointModal } from "@components/QontactPoints/NewQontactPointModal";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { usePopup } from "@contexts/popupContext";
import QontactPointDetailModalScreen from "@screens/QontactPoints/QontactPointDetailModalScreen";
import { QRMode } from "@screens/QontactPoints/QontactPointDetailModalViewModel";
import { HollowedButton, IconButton, NormalButton, RefreshButton } from "@shared/designSystem/Button";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import {
    HorizontalContainer,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import FilterPillGroup, { FilterPillOption } from "@shared/designSystem/FilterPillGroup";
import SearchBar from "@shared/designSystem/SearchBar";
import { SystemIcon } from "@shared/designSystem/SystemIcon";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { useNavigate } from "react-router-dom";
import {
    QONTACT_POINTS_PAGE_SIZE,
    QontactPointListFilter,
    QontactPointListItem,
    useQontactPointListScreenViewModel,
} from "./QontactPointListScreenViewModel";

interface QontactPointListScreenProps {
    orgID: string;
    isLite?: boolean;
    qrMode: QRMode;
}

const rowHeight = 76;
// Same layout as the collaborators list: percentage columns shared by header and rows
const columnWidths = {
    qontactPoint: "58%",
    turnos: "28%",
    actions: "14%",
};

const formatCoordinate = (value?: number) =>
    typeof value === "number" && !Number.isNaN(value) ? value.toFixed(6) : "—";

const shiftsLabel = (total: number) => {
    if (total === 0) return "Sin turnos".localized();
    if (total === 1) return "1 turno".localized();
    return "%s turnos".localized(String(total));
};

/**
 * Qontact Points list (design 1): search, Todos / Con turnos / Sin turnos filter,
 * "Nuevo Qontact Point" modal and delete confirmation. Data comes from the Algolia
 * search as today (shiftTotal of the index).
 */
const QontactPointListScreen: React.FC<QontactPointListScreenProps> = ({ orgID, isLite, qrMode }) => {
    const viewModel = useQontactPointListScreenViewModel(orgID);
    const { showPopup } = usePopup();
    const [isNewModalOpen, setIsNewModalOpen] = useState(false);
    const navigate = useNavigate();

    const openDetail = (qontactPoint: QontactPointListItem, defaultScreen?: string) => {
        navigate(`/qontactPoint/?id=${encodeURIComponent(qontactPoint.id)}`, {
            state: defaultScreen ? { qontactPoint, defaultScreen } : { qontactPoint },
        });
    };

    const openShifts = (qontactPoint: QontactPointListItem) => {
        if (isLite) {
            showPopup(() => <LiteVersionPopup />);
            return;
        }
        openDetail(qontactPoint, "turnos");
    };

    const openQR = (qontactPoint: QontactPointListItem) => {
        showPopup(() => <QontactPointDetailModalScreen qontactPoint={qontactPoint} qrMode={qrMode} />, false);
    };

    const filterOptions: FilterPillOption[] = [
        { id: "all", label: "Todos".localized(), count: viewModel.counts.all },
        { id: "with", label: "Con turnos".localized(), count: viewModel.counts.withShifts },
        { id: "without", label: "Sin turnos".localized(), count: viewModel.counts.withoutShifts },
    ];

    const newButton = viewModel.canManage ? (
        <NormalButton
            text={"Nuevo Qontact Point".localized()}
            onClick={() => setIsNewModalOpen(true)}
            width="220px"
        />
    ) : null;

    const isSearching = viewModel.searchField !== "" || viewModel.filter !== "all";
    const hasNoQontactPoints = !viewModel.loading && viewModel.total === 0;

    const renderRow = (index: number, style: React.CSSProperties) => {
        const qontactPoint = viewModel.qontactPoints[index];
        if (!qontactPoint) {
            return <div style={style} />;
        }
        // The virtualized list positions each row absolutely; the DS row inside keeps
        // the same columns, padding and gap as the header.
        return (
            <div style={{ ...style, display: "flex", alignItems: "center" }}>
                <TableRowContainer>
                <HorizontalContainer width={columnWidths.qontactPoint} minWidth="0" gap={designSystemStyles.gapMd}>
                    <IconButton
                        iconType="qr"
                        tooltip={"Ver QR".localized()}
                        onClick={() => openQR(qontactPoint)}
                    />
                    {/* margin 0: the DS default ("0 auto") would center the name in the column */}
                    <VerticalContainer
                        width="auto"
                        minWidth="0"
                        margin="0"
                        alignment="flex-start"
                        gap={designSystemStyles.gapXxxSm}
                        padding={designSystemStyles.paddingZero}
                    >
                        <ClickableWrapper onClick={() => openDetail(qontactPoint)} title={qontactPoint.name}>
                            <Text text={`**${qontactPoint.name.trim()}**`} markdown={true} truncate={true} />
                        </ClickableWrapper>
                        <SmallText
                            text={`${formatCoordinate(qontactPoint.latitude)}, ${formatCoordinate(qontactPoint.longitude)}`}
                            color={designSystemStyles.colorTextSecondary}
                        />
                    </VerticalContainer>
                </HorizontalContainer>
                <HorizontalContainer width={columnWidths.turnos}>
                    <ClickableWrapper onClick={() => openShifts(qontactPoint)} title={"Ver turnos".localized()}>
                        <Text
                            text={shiftsLabel(qontactPoint.totalShifts)}
                            color={qontactPoint.totalShifts === 0 ? designSystemStyles.colorTextSecondary : designSystemStyles.colorText}
                        />
                    </ClickableWrapper>
                </HorizontalContainer>
                <HorizontalContainer
                    width={columnWidths.actions}
                    justifyContent="right"
                    gap={designSystemStyles.gapSm}
                >
                    {viewModel.canManage && (
                        <>
                            <IconButton
                                iconType="edit"
                                tooltip={"Editar".localized()}
                                onClick={() => openDetail(qontactPoint)}
                            />
                            <IconButton
                                iconType="delete"
                                tooltip={"Eliminar".localized()}
                                onClick={() => viewModel.askDelete(qontactPoint)}
                            />
                        </>
                    )}
                </HorizontalContainer>
                </TableRowContainer>
            </div>
        );
    };

    const list = viewModel.loading ? (
        <QontyLoading text={"Cargando Qontact Points…".localized()} withQonty={true} darkMode={false} />
    ) : hasNoQontactPoints && !isSearching ? (
        <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center" gap={designSystemStyles.gapMd}>
            <QontyEmptyState text={"No hay Qontact Points".localized()} darkMode={false} fullSize={false} />
            <SmallText
                text={"Creá el primero para que tus colaboradores puedan fichar.".localized()}
                color={designSystemStyles.colorTextSecondary}
            />
            {newButton}
        </VerticalContainer>
    ) : hasNoQontactPoints ? (
        <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
            <QontyEmptyState
                text={"Ningún Qontact Point coincide con la búsqueda".localized()}
                darkMode={false}
                fullSize={false}
            />
        </VerticalContainer>
    ) : (
        <VirtualizedList
            key={viewModel.listKey}
            itemCount={viewModel.total}
            isItemLoaded={viewModel.isItemLoaded}
            loadMoreItems={viewModel.loadMoreItems}
            pageSize={QONTACT_POINTS_PAGE_SIZE}
            itemSize={rowHeight}
            renderRow={renderRow}
        />
    );

    const content = (
        <VerticalContainer gap={designSystemStyles.gapMd} alignment="left" height="100%" width="100%">
            <HorizontalContainer height={designSystemStyles.sizeRowMedium} width="100%" gap={designSystemStyles.gapMd}>
                <SearchBar
                    placeholder={"Buscar por nombre".localized()}
                    value={viewModel.searchInput}
                    onChange={viewModel.onSearchChange}
                />
                <RefreshButton text="" onClick={viewModel.refresh} />
                <HollowedButton
                    text={"Exportar".localized()}
                    onClick={viewModel.exportExcel}
                    disabled={viewModel.loading || viewModel.isExporting || viewModel.total === 0}
                    loading={viewModel.isExporting}
                    width="140px"
                />
                {newButton}
            </HorizontalContainer>
            <FilterPillGroup
                options={filterOptions}
                selectedId={viewModel.filter}
                onSelect={(id) => viewModel.setFilter(id as QontactPointListFilter)}
                ariaLabel={"Filtrar por turnos".localized()}
                size="small"
                wrap={false}
                isLoading={viewModel.loading}
            />
            <TableHeaderContainer height={designSystemStyles.sizeRowMedium}>
                <TableHeaderCell text={"Qontact Point".localized()} width={columnWidths.qontactPoint} />
                <TableHeaderCell text={"Turnos".localized()} width={columnWidths.turnos} />
                {/* Empty on purpose (like the collaborators list); a plain container avoids the stray "·" of an empty bold cell */}
                <HorizontalContainer width={columnWidths.actions}>{null}</HorizontalContainer>
            </TableHeaderContainer>
            {/* Bounded slot: the list fills what remains and scrolls inside */}
            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, width: "100%" }}>
                {list}
            </div>

            <NewQontactPointModal
                isOpen={isNewModalOpen}
                orgID={orgID}
                onClose={() => setIsNewModalOpen(false)}
                onCreated={viewModel.refresh}
            />
            <DeleteQontactPointModal
                isOpen={viewModel.qontactPointToDelete !== null}
                qontactPointName={viewModel.qontactPointToDelete?.name ?? ""}
                onClose={viewModel.cancelDelete}
                onConfirm={viewModel.confirmDelete}
            />
        </VerticalContainer>
    );

    return (
        <SingleTabScreen
            title={"Qontact Points".localized()}
            subtitle={"Gestión de Qontact Points".localized()}
            flap={{ id: "listado", label: "Listado".localized(), content }}
        />
    );
};

export default QontactPointListScreen;
